'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

/* ─── Data ─────────────────────────────────────────────────────────────── */
const features = [
  {
    id: 'f1',
    icon: 'CpuChipIcon',
    iconColor: 'text-sky-400',
    title: 'Multi-Agent Consensus',
    desc: 'OpenAI, Anthropic, Google & DeepSeek audit your contract in parallel. A cross-audit stage pairs every agent against every other - then a consensus score determines if the contract is safe to deploy.',
    border: 'border-neon-mid',
    tag: 'Core Engine',
    wide: true,
  },
  {
    id: 'f2',
    icon: 'ShieldCheckIcon',
    iconColor: 'text-amber-400',
    title: 'Static Analysis',
    desc: 'Slither + Mythril for EVM. Cargo Audit + Clippy for Solana/Rust. Every vulnerability class covered.',
    border: 'border-neon-mid',
    tag: 'Security',
    wide: false,
  },
  {
    id: 'f3',
    icon: 'BeakerIcon',
    iconColor: 'text-violet-400',
    title: 'Test Generation',
    desc: 'Auto-generate Foundry, Hardhat, or Anchor test suites. Fuzz tests, invariant tests, unit tests - all from your contract.',
    border: 'border-neon-mid',
    tag: 'Testing',
    wide: false,
  },
  {
    id: 'f4',
    icon: 'RocketLaunchIcon',
    iconColor: 'text-orange-400',
    title: 'One-Click Deployment',
    desc: 'Testnet & mainnet deployment with safety gates. 8-point checklist must pass before mainnet unlock.',
    border: 'border-neon-mid',
    tag: 'Deploy',
    wide: false,
  },
  {
    id: 'f5',
    icon: 'ClockIcon',
    iconColor: 'text-cyan-400',
    title: 'Version Control',
    desc: 'Every AI generation and manual edit creates an immutable snapshot. Diff, compare, restore - full audit trail.',
    border: 'border-neon-mid',
    tag: 'History',
    wide: false,
  },
  {
    id: 'f6',
    icon: 'UsersIcon',
    iconColor: 'text-rose-400',
    title: 'Team Collaboration',
    desc: 'Owner, Admin, Developer, Auditor, Viewer roles. Invite teammates, manage permissions, share workspaces.',
    border: 'border-neon-mid',
    tag: 'Teams',
    wide: true,
  },
];

const pricingTiers = [
  {
    id: 'free',
    name: 'Starter',
    price: '$0',
    period: '/mo',
    desc: 'For solo developers exploring AI-assisted auditing.',
    cta: 'Start Free',
    ctaHref: '/',
    highlight: false,
    credits: '500 credits/mo',
    features: [
      '500 AI credits per month',
      '3 active projects',
      'OpenAI + Google agents',
      'Testnet deployment',
      'Static analysis (Slither)',
      'Community support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49',
    period: '/mo',
    desc: 'For professional auditors and Web3 teams shipping to mainnet.',
    cta: 'Start Pro Trial',
    ctaHref: '/',
    highlight: true,
    badge: 'Most Popular',
    credits: '5,000 credits/mo',
    features: [
      '5,000 AI credits per month',
      'Unlimited projects',
      'All 4 AI agents (OpenAI, Anthropic, Google, DeepSeek)',
      'Mainnet deployment with safety gates',
      'Full static analysis suite',
      'Test generation (Foundry + Hardhat)',
      'Requirement drift monitoring',
      'Team collaboration (up to 5)',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For protocols, DAOs, and audit firms with custom SLAs.',
    cta: 'Contact Sales',
    ctaHref: 'mailto:sales@smartcontrax.io',
    highlight: false,
    credits: 'Unlimited',
    features: [
      'Unlimited AI credits',
      'Custom AI agent configuration',
      'Private deployment infrastructure',
      'SSO / SAML integration',
      'Dedicated audit environment',
      'SLA-backed uptime (99.9%)',
      'Unlimited team members',
      'Custom API rate limits',
      'Dedicated success manager',
    ],
  },
];

const testimonials = [
  {
    id: 't1',
    quote: 'Smartcontrax caught a reentrancy vulnerability in our vault contract that two manual auditors missed. The multi-agent consensus approach is genuinely different.',
    author: 'Marcus Chen',
    role: 'Lead Solidity Dev',
    company: 'DeFi Protocol',
    initials: 'MC',
  },
  {
    id: 't2',
    quote: 'We went from "contract written" to "deployed on Arbitrum mainnet" in under 4 hours. The safety gate checklist gave our DAO the confidence to approve the deployment.',
    author: 'Priya Nair',
    role: 'Smart Contract Auditor',
    company: 'Web3 Security Firm',
    initials: 'PN',
  },
  {
    id: 't3',
    quote: 'The test generation alone saves us 2-3 days per audit cycle. Foundry fuzz tests generated from the contract spec - it\'s the future of Web3 QA.',
    author: 'Alex Kowalski',
    role: 'Blockchain Engineer',
    company: 'Layer 2 Startup',
    initials: 'AK',
  },
];

const ecosystems = [
  { id: 'e1', label: 'Ethereum', icon: '⟠' },
  { id: 'e2', label: 'Solana', icon: '◎' },
  { id: 'e3', label: 'Base', icon: '🔵' },
  { id: 'e4', label: 'Arbitrum', icon: '🔷' },
  { id: 'e5', label: 'Polygon', icon: '⬡' },
  { id: 'e6', label: 'Optimism', icon: '🔴' },
];

/* ─── Navbar ────────────────────────────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md border-b border-neon-dark' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <AppLogo size={28} />
            <span className="font-semibold text-white tracking-tight">Smartcontrax</span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {['Features', 'Pricing', 'Testimonials'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-sm text-[#a8c8e8] hover:text-white transition-colors duration-300"
              >
                {item}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link href="/auth" className="text-sm text-[#a8c8e8] hover:text-white transition-colors duration-300">
              Sign In
            </Link>
            <Link href="/auth" className="btn-primary text-xs px-4 py-2">
              Start Free
              <Icon name="ArrowRightIcon" size={14} />
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-[#a8c8e8] hover:text-white transition-colors duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-black border-b border-neon-dark px-4 pb-4 space-y-2">
          {['Features', 'Pricing', 'Testimonials'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-sm text-[#a8c8e8] hover:text-white transition-colors duration-300"
            >
              {item}
            </a>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            <Link href="/auth" className="btn-secondary justify-center text-sm">Sign In</Link>
            <Link href="/auth" className="btn-primary justify-center text-sm">
              Start Free <Icon name="ArrowRightIcon" size={14} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ─── Hero ──────────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative flex flex-col overflow-hidden pt-16 bg-black">
      <div className="min-h-screen flex items-center">
        {/* Background grid */}
        <div className="absolute inset-0 bg-grid-pattern opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon-dark/20 border border-neon-mid text-xs font-medium mb-6 tag-loop-anim">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                4 AI Agents - Real-Time Consensus
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-6">
                <span className="text-white">From first line to</span>
                <br />
                <span className="text-[#39FF14]">mainnet deployment.</span>
              </h1>

              <p className="text-base sm:text-lg text-[#a8c8e8] leading-relaxed mb-8 max-w-lg border-l-2 border-[#0b6600] pl-4">
                Smartcontrax covers every stage of the smart contract lifecycle.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <Link href="/auth" className="btn-primary text-base px-6 py-3 justify-center sm:justify-start">
                  Start Building Free
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <a href="#features" className="btn-secondary text-base px-6 py-3 justify-center sm:justify-start">
                  <Icon name="PlayCircleIcon" size={16} />
                  See How It Works
                </a>
              </div>
            </div>

            {/* Right - terminal mockup */}
            <div className="relative hidden lg:block">
              <div className="rounded-xl overflow-hidden border border-neon-mid bg-black">
                {/* Terminal header */}
                <div className="flex items-center gap-2 px-4 py-3 bg-black border-b border-neon-dark">
                  <div className="w-3 h-3 rounded-full bg-red-500/70" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                  <div className="w-3 h-3 rounded-full bg-neon-mid/70" />
                  <span className="ml-2 text-xs text-[#a8c8e8] font-mono">consensus_audit.sol</span>
                </div>
                {/* Terminal body */}
                <div className="p-5 font-mono text-xs space-y-2 bg-black">
                  <div className="text-[#a8c8e8]">// Multi-Agent Consensus Audit</div>
                  <div className="text-sky-400">{'[OpenAI]'} <span className="text-white">Analyzing reentrancy patterns...</span></div>
                  <div className="text-violet-400">{'[Anthropic]'} <span className="text-white">Checking integer overflow...</span></div>
                  <div className="text-amber-400">{'[Google]'} <span className="text-white">Validating access controls...</span></div>
                  <div className="text-cyan-400">{'[DeepSeek]'} <span className="text-white">Cross-auditing findings...</span></div>
                  <div className="mt-3 pt-3 border-t border-neon-dark">
                    <div className="text-[#a8c8e8]">// Consensus Score</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-2 bg-black border border-neon-dark rounded-full overflow-hidden">
                        <div className="h-full w-[87%] rounded-full bg-neon-mid" />
                      </div>
                      <span className="text-neon-bright font-bold">87%</span>
                    </div>
                  </div>
                  <div className="mt-2 p-2 rounded-lg bg-neon-dark/20 border border-neon-mid">
                    <span className="text-neon-bright">&#10003; 2 Critical findings resolved - Safe to deploy</span>
                  </div>
                </div>
              </div>
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 px-3 py-1.5 rounded-full bg-black border border-neon-mid text-xs font-medium text-neon-bright">
                🔒 Mainnet Ready
              </div>
              <div className="absolute -bottom-4 -left-4 px-3 py-1.5 rounded-full bg-black border border-neon-mid text-xs font-medium text-neon-bright">
                ⚡ 4 Agents - 12 Cross-Audits
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trust Triangle Bridge ─────────────────────────────────────────── */}
      <div className="relative z-10 w-full pb-0">
        {/* Connector lines from hero to triangle */}
        <div className="flex justify-center">
          <div className="w-px h-8 bg-neon-dark/60" />
        </div>

        {/* Triangle structure */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top apex node */}
          <div className="flex justify-center mb-0">
            <div
              className="relative group"
              style={{
                filter: 'drop-shadow(0 0 12px rgba(57,255,20,0.25))',
              }}
            >
              {/* Apex card */}
              <div className="relative px-6 py-4 bg-black border border-neon-mid rounded-xl text-center min-w-[180px]"
                style={{
                  boxShadow: '0 0 20px rgba(57,255,20,0.12), inset 0 1px 0 rgba(57,255,20,0.15)',
                  transform: 'perspective(600px) rotateX(4deg)',
                }}
              >
                {/* 3D top edge highlight */}
                <div className="absolute top-0 left-4 right-4 h-px bg-neon-bright/40 rounded-full" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-mid mb-1">Encryption</p>
                <p className="text-sm font-bold text-white">TLS 1.3 Encrypted</p>
              </div>
            </div>
          </div>

          {/* SVG connecting lines */}
          <div className="relative flex justify-center" style={{ height: '60px' }}>
            <svg
              viewBox="0 0 400 60"
              className="w-full max-w-[500px]"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              {/* Left diagonal line: apex center → bottom-left node */}
              <line
                x1="200" y1="0"
                x2="60" y2="60"
                stroke="rgba(57,255,20,0.35)"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              {/* Right diagonal line: apex center → bottom-right node */}
              <line
                x1="200" y1="0"
                x2="340" y2="60"
                stroke="rgba(57,255,20,0.35)"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              {/* Horizontal base line connecting bottom two nodes */}
              <line
                x1="60" y1="60"
                x2="340" y2="60"
                stroke="rgba(57,255,20,0.25)"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              {/* Apex dot */}
              <circle cx="200" cy="0" r="3" fill="rgba(57,255,20,0.6)" />
              {/* Bottom-left dot */}
              <circle cx="60" cy="60" r="3" fill="rgba(57,255,20,0.6)" />
              {/* Bottom-right dot */}
              <circle cx="340" cy="60" r="3" fill="rgba(57,255,20,0.6)" />
            </svg>
          </div>

          {/* Bottom two nodes */}
          <div className="flex justify-between items-start gap-4 -mt-1">
            {/* Bottom-left */}
            <div
              className="relative flex-1 max-w-[200px]"
              style={{
                filter: 'drop-shadow(0 0 10px rgba(57,255,20,0.2))',
              }}
            >
              <div
                className="px-5 py-4 bg-black border border-neon-mid rounded-xl text-center"
                style={{
                  boxShadow: '0 0 18px rgba(57,255,20,0.10), inset 0 1px 0 rgba(57,255,20,0.12)',
                  transform: 'perspective(600px) rotateX(-3deg) rotateY(4deg)',
                }}
              >
                <div className="absolute top-0 left-3 right-3 h-px bg-neon-bright/30 rounded-full" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-mid mb-1">Isolation</p>
                <p className="text-sm font-bold text-white">Sandboxed Execution</p>
              </div>
            </div>

            {/* Center label */}
            <div className="flex flex-col items-center justify-center pt-2 px-2 shrink-0">
              <div className="w-px h-4 bg-neon-dark/50" />
              <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-neon-mid/60 my-1">secured by</span>
              <div className="w-px h-4 bg-neon-dark/50" />
            </div>

            {/* Bottom-right */}
            <div
              className="relative flex-1 max-w-[200px]"
              style={{
                filter: 'drop-shadow(0 0 10px rgba(57,255,20,0.2))',
              }}
            >
              <div
                className="px-5 py-4 bg-black border border-neon-mid rounded-xl text-center"
                style={{
                  boxShadow: '0 0 18px rgba(57,255,20,0.10), inset 0 1px 0 rgba(57,255,20,0.12)',
                  transform: 'perspective(600px) rotateX(-3deg) rotateY(-4deg)',
                }}
              >
                <div className="absolute top-0 left-3 right-3 h-px bg-neon-bright/30 rounded-full" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-mid mb-1">Compliance</p>
                <p className="text-sm font-bold text-white">SOC 2 Compliant</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bridge connector down to next section */}
        <div className="flex justify-center mt-6">
          <div className="flex flex-col items-center gap-0">
            <div className="w-px h-6 bg-neon-dark/50" />
            <div className="w-2 h-2 rounded-full border border-neon-mid bg-black" />
            <div className="w-px h-6 bg-neon-dark/30" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Features ──────────────────────────────────────────────────────────── */
function FeaturesSection() {
  return (
    <section id="features" aria-labelledby="features-heading" className="py-24 scroll-mt-16 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-4 tag-loop-anim">
            <Icon name="SparklesIcon" size={12} aria-hidden="true" />
            Full-Stack Web3 Engineering
          </div>
          <h2 id="features-heading" className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Everything you need to ship
            <br />
            <span className="text-gradient-primary">secure smart contracts</span>
          </h2>
          <p className="text-[#a8c8e8] max-w-xl mx-auto">
            From first line of Solidity to mainnet deployment - Smartcontrax covers every stage of the smart contract lifecycle.
          </p>
        </div>

        {/* Bento grid */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0">
          {features.map((f) => (
            <li
              key={f.id}
              className={`relative rounded-xl border ${f.border} bg-black p-6 flex flex-col gap-4 hover:border-neon-bright hover:shadow-glow-primary transition-all duration-500 ${
                f.wide ? 'sm:col-span-2 lg:col-span-2' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-black border border-neon-mid flex items-center justify-center" aria-hidden="true">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={20} className={f.iconColor} aria-hidden="true" />
                </div>
                <span className="text-2xs font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border tag-loop-anim">
                  {f.tag}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-[#a8c8e8] leading-relaxed">{f.desc}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* Ecosystem strip */}
        <div className="mt-16 text-center">
          <p className="text-xs text-[#a8c8e8] uppercase tracking-widest mb-6 font-medium">
            Supported Ecosystems
          </p>
          <ul className="flex flex-wrap justify-center gap-3 list-none p-0" aria-label="Supported blockchain ecosystems">
            {ecosystems.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black border border-neon-dark text-sm font-medium text-[#a8c8e8] hover:border-neon-bright hover:text-white transition-all duration-500"
              >
                {e.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing ───────────────────────────────────────────────────────────── */
function PricingSection() {
  return (
    <section id="pricing" aria-labelledby="pricing-heading" className="py-24 bg-black scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Pay for what you use.
            <br />
            <span className="text-gradient-primary">Scale as you grow.</span>
          </h2>
          <p className="text-[#a8c8e8] max-w-md mx-auto">
            Credits are consumed per AI operation. No hidden fees, no per-seat pricing surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {pricingTiers.map((tier) => (
            <article
              key={tier.id}
              aria-label={`${tier.name} plan`}
              className={`relative rounded-2xl border p-6 flex flex-col gap-5 bg-black transition-all duration-500 ${
                tier.highlight
                  ? 'border-neon-bright shadow-glow-primary'
                  : 'border-neon-dark hover:border-neon-mid'
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-neon-mid text-black text-xs font-semibold" aria-label={tier.badge}>
                  {tier.badge}
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-white uppercase tracking-widest mb-1">{tier.name}</p>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold text-white">{tier.price}</span>
                  {tier.period && <span className="text-white text-sm">{tier.period}</span>}
                </div>
                <p className="text-sm text-white">{tier.desc}</p>
              </div>

              <div className="px-3 py-2 rounded-lg bg-neon-dark/20 border border-neon-mid text-xs font-medium text-neon-bright text-center">
                {tier.credits}
              </div>

              <ul className="space-y-2.5 flex-1" aria-label={`${tier.name} features`}>
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#a8c8e8]">
                    <Icon name="CheckIcon" size={14} className="text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href={tier.ctaHref}
                className={`w-full text-center py-2.5 rounded-lg text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-bright focus-visible:ring-offset-2 ${
                  tier.highlight ? 'btn-neon-solid' : 'btn-neon'
                }`}
                aria-label={`${tier.cta} - ${tier.name} plan`}
              >
                {tier.cta}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ───────────────────────────────────────────────────────────────── */
function CTASection() {
  return (
    <section aria-labelledby="cta-heading" className="py-24 relative overflow-hidden bg-black">
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 id="cta-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
          Your next contract ships
          <br />
          <span className="text-gradient-primary">without vulnerabilities.</span>
        </h2>
        <p className="text-lg text-[#a8c8e8] mb-10 max-w-xl mx-auto">
          Join thousands of Web3 developers who trust Smartcontrax to audit, test, and deploy their smart contracts.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link href="/auth" className="btn-primary text-base px-8 py-3.5 justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-bright focus-visible:ring-offset-2">
            Start Building Free
            <Icon name="ArrowRightIcon" size={16} aria-hidden="true" />
          </Link>
          <a href="#pricing" className="btn-secondary text-base px-8 py-3.5 justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-bright focus-visible:ring-offset-2">
            View Pricing
          </a>
        </div>
        <p className="mt-6 text-xs text-[#a8c8e8]">
          No credit card required - 500 free credits - Cancel anytime
        </p>
      </div>
    </section>
  );
}

/* ─── Footer ────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer role="contentinfo" className="border-t border-neon-dark bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="flex items-center gap-2.5">
            <AppLogo size={24} />
            <span className="font-semibold text-sm text-white">Smartcontrax</span>
            <span className="text-xs text-[#a8c8e8] ml-2">© 2026</span>
          </div>
          <nav aria-label="Footer navigation">
            <ul className="flex flex-wrap gap-6 text-xs text-[#a8c8e8] list-none p-0">
              {['Privacy Policy', 'Terms of Service', 'Security', 'Docs', 'Status'].map((link) => (
                <li key={link}>
                  <a href="#" className="hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-bright rounded">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}

/* ─── Structured Data ───────────────────────────────────────────────────── */
function StructuredData() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Smartcontrax',
    applicationCategory: 'DeveloperApplication',
    description:
      'AI-powered smart contract engineering platform with multi-agent consensus auditing, static analysis, test generation, and one-click deployment.',
    url: 'https://smartcontr9298.builtwithrocket.new',
    offers: [
      { '@type': 'Offer', name: 'Starter', price: '0', priceCurrency: 'USD' },
      { '@type': 'Offer', name: 'Pro', price: '49', priceCurrency: 'USD' },
    ],
    featureList: [
      'Multi-Agent AI Consensus',
      'Smart Contract Auditing',
      'Static Analysis',
      'Test Generation',
      'One-Click Deployment',
      'Team Collaboration',
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/* ─── Main Export ───────────────────────────────────────────────────────── */
export default function LandingPage() {
  return (
    <>
      <StructuredData />
      <div className="min-h-screen bg-black text-foreground">
        <Navbar />
        <main>
          <Hero />
          <FeaturesSection />
          <PricingSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}
